import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { DefinitionAuditRow } from '@marque/api/central'

// AB#2123 — the ops-portal per-module change feed: every designer save, mpack install, promotion, rollback,
// retire, uninstall and delete of a catalogue object owned by (or keyed to) this module, newest first,
// filterable by object type and source. It COMPOSES with the AB#2092 promotion-audit surface rather than
// duplicating it: a promotion lands here as a `source=promotion` row, so the promotion-audit view can link to
// these object-level rows instead of persisting a second copy. Greenfield — no module-detail page exists in the
// central app yet, so this is a drop-in panel that AB#2092's ops surface (or a future module page) can host.

const OBJECT_TYPES = ['entity', 'process', 'view', 'module', 'module-view', 'integration-endpoint', 'template'] as const
const SOURCES = [
    'designer-save', 'designer-publish', 'mpack-install', 'promotion', 'rollback', 'retire', 'uninstall', 'delete',
] as const

// Muted badge palette; destructive sources share the red family.
const SOURCE_STYLE: Record<string, { bg: string; fg: string }> = {
    'designer-save': { bg: '#dbeafe', fg: '#1e40af' },
    'designer-publish': { bg: '#dcfce7', fg: '#166534' },
    'mpack-install': { bg: '#fef9c3', fg: '#854d0e' },
    promotion: { bg: '#ede9fe', fg: '#5b21b6' },
    rollback: { bg: '#ffedd5', fg: '#9a3412' },
    retire: { bg: '#fee2e2', fg: '#991b1b' },
    uninstall: { bg: '#fee2e2', fg: '#991b1b' },
    delete: { bg: '#fee2e2', fg: '#991b1b' },
}

function SourceBadge({ source }: { source: string }) {
    const s = SOURCE_STYLE[source] ?? { bg: '#f1f5f9', fg: '#475569' }
    return (
        <span
            className="px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
            style={{ background: s.bg, color: s.fg }}
        >
            {source}
        </span>
    )
}

/**
 * AB#2123 — renders the change feed for one module.
 * @param moduleId a module_packages.id (Guid), the same id the module-detail / upgrade-impacts endpoints take.
 * @param promotionAuditHref optional deep link to the AB#2092 promotion-audit surface, shown on promotion/
 *        rollback rows so the two surfaces compose rather than duplicate.
 */
export function ModuleChangeFeed({
    moduleId,
    promotionAuditHref,
}: {
    moduleId: string
    promotionAuditHref?: string
}) {
    const [objectType, setObjectType] = useState<string>('')
    const [source, setSource] = useState<string>('')

    const { data, isLoading, isError } = useQuery({
        queryKey: ['module-definition-audit', moduleId, objectType, source],
        queryFn: () => {
            // exactOptionalPropertyTypes: only set a filter key when it has a value (an explicit
            // `undefined` value is not assignable to an optional-string property).
            const filters: { objectType?: string; source?: string } = {}
            if (objectType) { filters.objectType = objectType }
            if (source) { filters.source = source }
            return centralApi.getModuleDefinitionAudit(moduleId, filters)
        },
    })

    return (
        <section className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-neutral-800">Change history</h3>
                <select
                    data-testid="module-audit-object-type"
                    value={objectType}
                    onChange={(e) => setObjectType(e.target.value)}
                    className="border border-neutral-300 rounded px-2 py-1 text-xs"
                >
                    <option value="">All objects</option>
                    {OBJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select
                    data-testid="module-audit-source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="border border-neutral-300 rounded px-2 py-1 text-xs"
                >
                    <option value="">All sources</option>
                    {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            {isLoading && <p className="text-xs text-neutral-500">Loading change history…</p>}
            {isError && <p className="text-xs text-red-600">Could not load the change history.</p>}
            {data && data.length === 0 && (
                <p className="text-xs text-neutral-500">No changes recorded for this module yet.</p>
            )}

            {data && data.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            <tr className="text-left text-neutral-500 border-b border-neutral-200">
                                <th className="py-1.5 pr-3 font-medium">When</th>
                                <th className="py-1.5 pr-3 font-medium">Object</th>
                                <th className="py-1.5 pr-3 font-medium">Source</th>
                                <th className="py-1.5 pr-3 font-medium">Author</th>
                                <th className="py-1.5 pr-3 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row: DefinitionAuditRow) => (
                                <tr key={row.id} className="border-b border-neutral-100">
                                    <td className="py-1.5 pr-3 text-neutral-500 whitespace-nowrap">
                                        {new Date(row.createdAt).toLocaleString()}
                                    </td>
                                    <td className="py-1.5 pr-3">
                                        <span className="text-neutral-400">{row.objectType}</span>
                                        {' '}
                                        <span className="font-mono text-neutral-800">{row.objectKey}</span>
                                    </td>
                                    <td className="py-1.5 pr-3"><SourceBadge source={row.source} /></td>
                                    <td className="py-1.5 pr-3 text-neutral-700">{row.author ?? 'unknown'}</td>
                                    <td className="py-1.5 pr-3">
                                        {promotionAuditHref && (row.source === 'promotion' || row.source === 'rollback') && (
                                            <a href={promotionAuditHref} className="text-blue-600 hover:underline">
                                                promotion detail →
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    )
}
